import { FoodSearchTier, searchTierOfFoodId } from '../data-model/food';

/** how many days a used food stays in the History tier before it starts to sink */
const HISTORY_FULL_DAYS = 14;
/** how many days it takes a used food to sink one tier after that */
const HISTORY_DAYS_PER_TIER = 7;

/** whether an id belongs to something a person made: their foods, recipes, meals and logged meals */
function isUserMadeId(foodId: string): boolean {
  return foodId.startsWith('food/+') || foodId.startsWith('meal/') || foodId.startsWith('log/');
}

/**
 * The tier a food gets from where it came from alone.
 *
 * A food a person made counts as their own only when it is stored on this device. Sharing a food
 * deletes its author stamp, so being on the device is the one test that still holds; the same id
 * found by the server is somebody else's contribution. Every other id is placed by its source's
 * own tier, which is a column of `foodSourceTable` in `shared/src/data-model/food.ts`.
 */
export function sourceTierOf(foodId: string, isOnDevice: boolean): FoodSearchTier {
  // if the person made it and it is on this device, it is theirs
  if (isUserMadeId(foodId)) {
    return isOnDevice ? FoodSearchTier.OwnFoods : FoodSearchTier.Contributed;
  }
  return searchTierOfFoodId(foodId);
}

/**
 * The tier a food gets from how long ago it was last used: History for the first 14 days, then
 * one tier lower for every week after that. Day 15-21 is tier 2, day 22-28 tier 3, and from day
 * 43 it is tier 6, below which nothing sinks.
 */
export function historyTierOf(daysSinceLastUse: number): number {
  // if it was used within the first two weeks, or the clock says the future, it is history
  if (daysSinceLastUse <= HISTORY_FULL_DAYS) {
    return FoodSearchTier.History;
  }
  return FoodSearchTier.History + Math.ceil((daysSinceLastUse - HISTORY_FULL_DAYS) / HISTORY_DAYS_PER_TIER);
}

/**
 * The tier a search list orders a food by: the better of its source tier and its history tier,
 * or just the source tier when it was never used. So a logged Open Food Facts food rises to the
 * top for two weeks and then sinks back week by week, while a trusted one never sinks below 4.
 */
export function effectiveTierOf(foodId: string, isOnDevice: boolean, daysSinceLastUse: number | undefined): FoodSearchTier {
  const sourceTier = sourceTierOf(foodId, isOnDevice);
  // if the food was never used, its source alone decides
  if (daysSinceLastUse === undefined || !Number.isFinite(daysSinceLastUse)) {
    return sourceTier;
  }
  return Math.min(sourceTier, historyTierOf(daysSinceLastUse));
}
